import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CarSpendDb,
  parseBackup,
  detectSourceCurrency,
  buildDemoStore,
  buildBackupZip,
  referencedAttachmentNames,
  type CanonicalStore,
  type Car,
} from "../core/index.ts";
import { LiveRates } from "./rates.ts";

export interface ImportResult {
  imported: boolean;
  skipped: number;
  reasons: string[];
}

/** Lightweight peek at how many cars a backup contains (before full parse). */
export function countCarsInBackup(fileText: string): number {
  try {
    const root = JSON.parse(fileText);
    const cars = Array.isArray((root as Record<string, unknown>)["cars"]) ? (root as Record<string, unknown>)["cars"] as unknown[] : [];
    return cars.length;
  } catch { return 0; }
}

/** Peek at car names in a backup (for the car picker). */
export function carsInBackup(fileText: string): { id: string; name: string }[] {
  try {
    const root = JSON.parse(fileText);
    const cars = Array.isArray((root as Record<string, unknown>)["cars"]) ? (root as Record<string, unknown>)["cars"] as { id?: string; make?: string; model?: string }[] : [];
    return cars.map((c) => ({ id: c?.id ?? "", name: `${c?.make ?? ""} ${c?.model ?? ""}`.trim().toUpperCase() || "UNKNOWN" }));
  } catch { return []; }
}

export function useCarspend() {
  const db = useMemo(() => new CarSpendDb(), []);
  const rates = useMemo(() => new LiveRates(), []);
  const [store, setStore] = useState<CanonicalStore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastExportAt, setLastExportAt] = useState<number | null>(() => {
    const v = localStorage.getItem("carspend_last_export");
    return v ? Number(v) : null;
  });
  /** When a free user imports a multi-car backup, we need them to pick one. */
  const [pendingImport, setPendingImport] = useState<{ text: string; cars: { id: string; name: string }[] } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        if (navigator.storage?.persist) void navigator.storage.persist();
        const empty = await db.isEmpty();
        setStore(empty ? null : await db.loadAll());
      } finally {
        setLoading(false);
      }
    })();
  }, [db]);

  /** Build a `.zip` backup Blob for a store (backup.json + referenced attachment blobs). */
  const buildZip = useCallback(
    async (s: CanonicalStore): Promise<Blob> => {
      const map = new Map<string, Uint8Array>();
      for (const name of referencedAttachmentNames(s)) {
        const blob = await db.getAttachment(name);
        if (blob) map.set(name, new Uint8Array(await blob.arrayBuffer()));
      }
      return buildBackupZip(s, map);
    },
    [db],
  );

  /** Full-replace import. If `carId` is set, only keep that car + its data.
   *  `attachments` (from a ZIP backup) are written after the records land. */
  const importBackup = useCallback(
    async (fileText: string, attachments?: Map<string, Uint8Array>, carId?: string): Promise<ImportResult> => {
      setError(null);
      if (store && store.cars.length > 0) {
        downloadBlob(await buildZip(store), `carspend-before-import-${Date.now()}.zip`);
      }
      let root: unknown;
      try { root = JSON.parse(fileText); } catch { root = {}; }
      const displayCurrency =
        detectSourceCurrency(root as Record<string, unknown>) ??
        store?.profile.displayCurrency ??
        "USD";
      const parsed = await parseBackup(fileText, { displayCurrency, rates });

      let result = parsed;
      if (carId) {
        const id = carId;
        result = {
          ...parsed,
          cars: parsed.cars.filter((c) => c.id === id),
          fuelLogs: parsed.fuelLogs.filter((l) => l.carId === id),
          spendings: parsed.spendings.filter((s) => s.carId === id),
          trips: parsed.trips.filter((t) => t.carId === id),
          tyres: parsed.tyres.filter((t) => t.carId === id),
          tyreSets: parsed.tyreSets.filter((ts) => ts.carId === id),
          maintenanceReminders: parsed.maintenanceReminders.filter((m) => m.carId === id),
        };
      }

      await db.replaceAll(result);
      // Full-replace semantics: drop old attachments, then write the ones this
      // backup references (ignore any extras not tied to a kept record).
      await db.clearAttachments();
      let attachmentsWritten = 0;
      if (attachments && attachments.size > 0) {
        const keep = new Set(referencedAttachmentNames(result));
        for (const [name, bytes] of attachments) {
          if (keep.has(name)) { await db.putAttachment(name, new Blob([bytes as BlobPart])); attachmentsWritten++; }
        }
      }
      // Diagnostic: how many attachment blobs landed vs. what the zip carried.
      console.info(
        `[CarSpend import] zip attachments=${attachments?.size ?? 0}, referenced=${referencedAttachmentNames(result).length}, written=${attachmentsWritten}`,
      );
      setStore(await db.loadAll());
      return { imported: true, skipped: parsed.skipped.length, reasons: parsed.skipped.map((s) => `${s.collection} ${s.id}: ${s.reason}`) };
    },
    [db, rates, store, buildZip],
  );

  /** Persist an edited store (full-replace into IndexedDB) and update state. */
  const save = useCallback(
    async (next: CanonicalStore) => {
      await db.replaceAll(next);
      setStore(next);
    },
    [db],
  );

  /** Replace everything with the built-in demo car (for visitors exploring). */
  const loadDemo = useCallback(async () => {
    setError(null);
    if (store && store.cars.length > 0) {
      downloadBlob(await buildZip(store), `carspend-before-demo-${Date.now()}.zip`);
    }
    await db.replaceAll(buildDemoStore());
    await db.clearAttachments();
    setStore(await db.loadAll());
  }, [db, store, buildZip]);

  const exportBackup = useCallback(async () => {
    if (!store) return;
    downloadBlob(await buildZip(store), "carspend-backup.zip");
    const now = Math.floor(Date.now() / 1000);
    localStorage.setItem("carspend_last_export", String(now));
    setLastExportAt(now);
  }, [store, buildZip]);

  const clearAll = useCallback(async () => {
    if (store && store.cars.length > 0) {
      downloadBlob(await buildZip(store), `carspend-before-clear-${Date.now()}.zip`);
    }
    await db.delete();
    await db.open();
    setStore(null);
  }, [db, store, buildZip]);

  return { loading, error, setError, store, importBackup, exportBackup, clearAll, save, lastExportAt, loadDemo };
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
